import React, { useEffect, useState } from "react";
import ClientLayout from "../../components/client/ClientLayout";
import { useAuth } from "../../context/AuthContext";
import { clientApi } from "../../api";
import AdminSelect from "../../components/AdminSelect";

export default function ProductsPage() {
  const { user } = useAuth();
  const isAdmin = user?.clientRole === "admin";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", price: "", currency: "aud", duration_months: 12 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [loadError, setLoadError] = useState("");

  async function load() {
    setLoading(true); setLoadError("");
    try { setProducts(await clientApi.getProducts() || []); }
    catch (err) { setLoadError(err.message || "Failed to load products."); }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  function openAdd() {
    setEdit(null);
    setForm({ name: "", description: "", price: "", currency: "aud", duration_months: 12 });
    setError(""); setShowModal(true);
  }
  function openEdit(p) {
    setEdit(p);
    setForm({ name: p.name, description: p.description || "", price: (p.price_cents / 100).toFixed(2), currency: p.currency || "aud", duration_months: p.duration_months || 12 });
    setError(""); setShowModal(true);
  }

  async function save(e) {
    e.preventDefault(); setSaving(true); setError("");
    try {
      const payload = { ...form, price_cents: Math.round(parseFloat(form.price || 0) * 100) };
      if (edit) await clientApi.updateProduct(edit.id, payload);
      else await clientApi.createProduct(payload);
      setShowModal(false); await load();
    } catch (err) { setError(err.message); }
    setSaving(false);
  }

  async function del(id) {
    if (!window.confirm("Delete this product?")) return;
    try { await clientApi.deleteProduct(id); setProducts((p) => p.filter((x) => x.id !== id)); }
    catch (err) { setLoadError(err.message || "Failed to delete product."); }
  }

  return (
    <ClientLayout>
      <div className="venues-page-header">
        <div>
          <h1>Membership Products</h1>
          <p className="page-subtitle">{isAdmin ? "Configure pricing plans" : "View membership plans (admins can edit)"}</p>
        </div>
        {isAdmin && <button className="btn btn-primary" onClick={openAdd}>+ Add Product</button>}
      </div>

      {loadError && <div className="toast toast-error" style={{ marginBottom: "1rem" }}>{loadError}</div>}

      {loading ? <div className="loading-state"><div className="spinner" /></div> : products.length ? (
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>Product</th><th>Price</th><th>Duration</th><th>Status</th>{isAdmin && <th><span className="sr-only">Actions</span></th>}</tr></thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{p.name}</div>
                    {p.description && <div style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>{p.description}</div>}
                  </td>
                  <td style={{ fontWeight: 700 }}>${(p.price_cents / 100).toFixed(2)} <span style={{ fontSize: "0.8rem", color: "var(--text-muted)", fontWeight: 400 }}>{(p.currency || "AUD").toUpperCase()}</span></td>
                  <td style={{ color: "var(--text-muted)" }}>{p.duration_months} mo</td>
                  <td><span className={`badge ${p.active ? "active" : "inactive"}`}>{p.active ? "Active" : "Inactive"}</span></td>
                  {isAdmin && (
                    <td>
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <button className="btn btn-outline btn-sm" onClick={() => openEdit(p)}>Edit</button>
                        <button className="btn btn-danger btn-sm" onClick={() => del(p.id)}>Delete</button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <h3>No products yet</h3>
          <p className="muted">Create a membership plan to start selling memberships through your signup page.</p>
          {isAdmin && <button className="btn btn-primary" onClick={openAdd}>+ Add Product</button>}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <div className="modal-header-text"><h2>{edit ? "Edit Product" : "Add Product"}</h2></div>
              <button type="button" className="modal-close-btn" onClick={() => setShowModal(false)} aria-label="Close">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <form onSubmit={save}>
              <div className="modal-body">
                {error && <div className="toast toast-error" style={{ marginBottom: "0.75rem" }}>{error}</div>}
                <div className="form-field">
                  <label htmlFor="product-name">Name <span className="required">*</span></label>
                  <input id="product-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                </div>
                <div className="form-field">
                  <label htmlFor="product-description">Description</label>
                  <input id="product-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>
                <div className="form-grid-2">
                  <div className="form-field">
                    <label htmlFor="product-price">Price <span className="required">*</span></label>
                    <div className="input-currency-wrap">
                      <span className="input-prefix">$</span>
                      <input id="product-price" type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required />
                      <span className="input-suffix">AUD</span>
                    </div>
                  </div>
                  <div className="form-field">
                    <label>Duration</label>
                    <AdminSelect value={form.duration_months} onChange={(v) => setForm({ ...form, duration_months: parseInt(v) })} options={[{value:1,label:"1 month"},{value:3,label:"3 months"},{value:6,label:"6 months"},{value:12,label:"12 months"},{value:24,label:"24 months"},{value:36,label:"36 months"},{value:60,label:"60 months"}]} />
                  </div>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Saving…" : edit ? "Update" : "Create"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </ClientLayout>
  );
}
